import { FormControlProps } from '@lsy-netline/netline-ui/FormControl';
import clsx from 'clsx';
import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useDarkMode from 'hooks/useDarkMode';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, styled } from 'netline-ui';
import services from 'services';
import { CloseableComponentProps } from 'types';
import { convertDarkModeStrToSelectedDarkMode } from 'utils';

import styles, { darkModeSelectorClasses } from './DarkModeSelector.styles';

const DarkModeSelectorRoot = styled(FormControl, { name: darkModeSelectorClasses.$name })(styles);

type DarkModeSelectorProps = CloseableComponentProps & FormControlProps;

const DarkModeSelector = forwardRef<HTMLDivElement, DarkModeSelectorProps>(
  ({ className, onMenuClose, ...props }, ref) => {
    const { t } = useTranslation();
    const { selectedDarkMode, darkModeAllowed } = useDarkMode();

    const handleSelectItem = useCallback(
      (event) => {
        services.appFrame.theme.setDarkModeSelection(
          convertDarkModeStrToSelectedDarkMode(event.currentTarget.dataset.value),
        );
        onMenuClose?.(event);
      },
      [onMenuClose],
    );

    return (
      <DarkModeSelectorRoot ref={ref} className={clsx(className, darkModeSelectorClasses.root)} {...props}>
        <FormLabel className={darkModeSelectorClasses.label}>{t('theme.title')}</FormLabel>
        <RadioGroup data-testid="DarkModeSelector-radioGroup" value={`${selectedDarkMode}`}>
          <MenuItem
            className={darkModeSelectorClasses.item}
            disabled={!darkModeAllowed}
            onClick={handleSelectItem}
            data-value="auto"
          >
            <FormControlLabel
              value="auto"
              control={<Radio size="small" disabled={!darkModeAllowed} data-testid="DarkModeSelector-autoRadio" />}
              label={t('theme.auto')}
            />
          </MenuItem>
          <MenuItem
            className={darkModeSelectorClasses.item}
            disabled={!darkModeAllowed}
            onClick={handleSelectItem}
            data-value="false"
          >
            <FormControlLabel
              value="false"
              control={<Radio size="small" disabled={!darkModeAllowed} data-testid="DarkModeSelector-lighRadio" />}
              label={t('theme.light')}
            />
          </MenuItem>
          <MenuItem
            className={darkModeSelectorClasses.item}
            disabled={!darkModeAllowed}
            onClick={handleSelectItem}
            data-value="true"
          >
            <FormControlLabel
              value="true"
              control={<Radio size="small" disabled={!darkModeAllowed} data-testid="DarkModeSelector-darkRadio" />}
              label={t('theme.dark')}
            />
          </MenuItem>
        </RadioGroup>
      </DarkModeSelectorRoot>
    );
  },
);

export default DarkModeSelector;
